import type { FetchQueryOptions, QueryClient, QueryKey } from "@tanstack/react-query";
import { isCancelledError } from "@tanstack/react-query";
import { clone, isEqual } from "lodash-es";

import { GenericStatusMessageStore } from "@framework/GenericStatusMessageStore";
import type { StatusMessage } from "@framework/ModuleInstanceStatusController";
import { StatusMessageStoreTopic, type StatusMessage as GenericStatusMessage } from "@framework/types/statusWriter";
import { ApiErrorHelper } from "@framework/utils/ApiErrorHelper";
import type { PublishSubscribe } from "@lib/utils/PublishSubscribeDelegate";
import { PublishSubscribeDelegate } from "@lib/utils/PublishSubscribeDelegate";
import { ScopedQueryController } from "@lib/utils/ScopedQueryController";
import { UnsubscribeFunctionsManagerDelegate } from "@lib/utils/UnsubscribeFunctionsManagerDelegate";

import { ItemDelegate } from "../../delegates/ItemDelegate";
import {
    SettingsContextDelegate,
    SettingsContextDelegateTopic,
    SettingsContextStatus,
} from "../../delegates/SettingsContextDelegate";
import type {
    CustomDataProviderImplementation,
    DataProviderAccessors,
} from "../../interfacesAndTypes/customDataProviderImplementation";
import type { Item } from "../../interfacesAndTypes/entities";
import { type SerializedDataProvider, SerializedType } from "../../interfacesAndTypes/serialization";
import type { NullableStoredData, StoredData } from "../../interfacesAndTypes/sharedTypes";
import type { MakeSettingTypesMap, SettingsKeysFromTuple } from "../../interfacesAndTypes/utils";
import type { Settings } from "../../settings/settingsDefinitions";
import { type DataProviderManager, DataProviderManagerTopic } from "../DataProviderManager/DataProviderManager";
import { makeSettings } from "../utils/makeSettings";

export enum DataProviderTopic {
    STATUS = "STATUS",
    DATA = "DATA",
    SUBORDINATED = "SUBORDINATED",
    REVISION_NUMBER = "REVISION_NUMBER",
    PROGRESS_MESSAGE = "PROGRESS_MESSAGE",
    STATUS_MESSAGES = "STATUS_MESSAGES",
}

export enum DataProviderStatus {
    IDLE = "IDLE",
    LOADING = "LOADING",
    ERROR = "ERROR",
    INVALID_SETTINGS = "INVALID_SETTINGS",
    SUCCESS = "SUCCESS",
}

export type DataProviderPayloads<TData> = {
    [DataProviderTopic.STATUS]: DataProviderStatus;
    [DataProviderTopic.DATA]: TData;
    [DataProviderTopic.SUBORDINATED]: boolean;
    [DataProviderTopic.REVISION_NUMBER]: number;
    [DataProviderTopic.PROGRESS_MESSAGE]: string | null;
    [DataProviderTopic.STATUS_MESSAGES]: readonly GenericStatusMessage[];
};

// Using a unique brand to identify DataProvider objects, since instanceof checks won't work due to potential multiple versions of the module.
// Using Symbol.for to ensure that even if there are multiple versions of the module, they will all reference the same symbol for the brand.
const DATA_PROVIDER_BRAND = Symbol.for("dpf/data-provider");

export function isDataProvider(obj: any): obj is DataProvider<any, any> {
    return typeof obj === "object" && obj !== null && DATA_PROVIDER_BRAND in obj;
}

export type DataProviderParams<
    TSettings extends Settings,
    TData,
    TStoredData extends StoredData = Record<string, never>,
    TSettingTypes extends MakeSettingTypesMap<TSettings> = MakeSettingTypesMap<TSettings>,
    TSettingKey extends SettingsKeysFromTuple<TSettings> = SettingsKeysFromTuple<TSettings>,
> = {
    type: string;
    dataProviderManager: DataProviderManager;
    instanceName?: string;
    customDataProviderImplementation: CustomDataProviderImplementation<
        TSettings,
        TData,
        TStoredData,
        TSettingTypes,
        TSettingKey
    >;
};

/*
 * The DataProvider class is responsible for managing the state of a data provider.
 * It is responsible for (re-)fetching the data whenever changes to settings make it necessary.
 * It also manages the status of the provider (loading, success, error).
 */
export class DataProvider<
    TSettings extends Settings,
    TData,
    TStoredData extends StoredData = Record<string, never>,
    TSettingTypes extends MakeSettingTypesMap<TSettings> = MakeSettingTypesMap<TSettings>,
    TSettingKey extends SettingsKeysFromTuple<TSettings> = SettingsKeysFromTuple<TSettings>,
>
    implements Item, PublishSubscribe<DataProviderPayloads<TData>>
{
    private readonly [DATA_PROVIDER_BRAND] = true;

    private _type: string;
    private _customDataProviderImpl: CustomDataProviderImplementation<
        TSettings,
        TData,
        TStoredData,
        TSettingTypes,
        TSettingKey
    >;
    private _settingsContextDelegate: SettingsContextDelegate<TSettings, TSettingTypes, TStoredData, TSettingKey>;
    private _itemDelegate: ItemDelegate;
    private _dataProviderManager: DataProviderManager;
    private _unsubscribeFunctionsManagerDelegate: UnsubscribeFunctionsManagerDelegate =
        new UnsubscribeFunctionsManagerDelegate();
    private _publishSubscribeDelegate = new PublishSubscribeDelegate<DataProviderPayloads<TData>>();
    private _status: DataProviderStatus = DataProviderStatus.IDLE;
    private _data: TData | null = null;
    private _error: StatusMessage | string | null = null;
    private _valueRange: readonly [number, number] | null = null;
    private _isSubordinated: boolean = false;
    private _prevSettings: TSettingTypes | null = null;
    private _prevStoredData: NullableStoredData<TStoredData> | null = null;
    private _currentTransactionId: number = 0;
    private _revisionNumber: number = 0;
    private _progressMessage: string | null = null;
    private _scopedQueryController: ScopedQueryController;
    private _debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private _onFetchCancelOrFinishFn: () => void = () => {};

    private _statusWriter = new GenericStatusMessageStore("DataProvider");
    private _allStatusMessages: GenericStatusMessage[] = [];

    constructor(params: DataProviderParams<TSettings, TData, TStoredData, TSettingTypes, TSettingKey>) {
        const {
            dataProviderManager: dataProviderManager,
            type,
            instanceName,
            customDataProviderImplementation,
        } = params;
        this._type = type;
        this._dataProviderManager = dataProviderManager;
        this._settingsContextDelegate = new SettingsContextDelegate<TSettings, TSettingTypes, TStoredData, TSettingKey>(
            customDataProviderImplementation,
            dataProviderManager,
            makeSettings<TSettings, TSettingTypes, TSettingKey>(
                customDataProviderImplementation.settings,
                customDataProviderImplementation.getDefaultSettingsValues?.() ?? {},
            ),
        );
        this._scopedQueryController = new ScopedQueryController(params.dataProviderManager.getQueryClient());
        this._customDataProviderImpl = customDataProviderImplementation;
        this._itemDelegate = new ItemDelegate(
            instanceName ?? customDataProviderImplementation.getDefaultName(),
            1,
            dataProviderManager,
        );

        this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
            "status-writer",
            this._statusWriter
                .getPublishSubscribeDelegate()
                .makeSubscriberFunction(StatusMessageStoreTopic.STATUS_MESSAGES)(() => this.syncAllStatusMessages()),
        );

        this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
            "settings-context",
            this._settingsContextDelegate
                .getPublishSubscribeDelegate()
                .makeSubscriberFunction(SettingsContextDelegateTopic.STATUS_MESSAGES)(() => {
                this.syncAllStatusMessages();
            }),
        );

        this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
            "settings-context",
            this._settingsContextDelegate
                .getPublishSubscribeDelegate()
                .makeSubscriberFunction(SettingsContextDelegateTopic.SETTINGS_AND_STORED_DATA_CHANGED)(() => {
                this.handleSettingsAndStoredDataChange();
            }),
        );

        this._unsubscribeFunctionsManagerDelegate.registerUnsubscribeFunction(
            "settings-context",
            this._settingsContextDelegate
                .getPublishSubscribeDelegate()
                .makeSubscriberFunction(SettingsContextDelegateTopic.STATUS)(() => {
                this.handleSettingsStatusChange();
            }),
        );
    }

    getRevisionNumber(): number {
        return this._revisionNumber;
    }

    areCurrentSettingsValid(): boolean {
        if (!this._customDataProviderImpl.areCurrentSettingsValid) {
            return true;
        }

        return this._customDataProviderImpl.areCurrentSettingsValid(this.makeAccessors());
    }

    private handleSettingsAndStoredDataChange(): void {
        this._statusWriter.clear();

        if (this._settingsContextDelegate.getStatus() === SettingsContextStatus.LOADING) {
            this.setStatus(DataProviderStatus.LOADING);
            return;
        }

        if (!this.areCurrentSettingsValid()) {
            this._error = "Invalid settings";
            this.setStatus(DataProviderStatus.INVALID_SETTINGS);
            return;
        }

        this.tidyUpFetchRelatedResources();

        let refetchRequired;

        if (this._customDataProviderImpl.doSettingsChangesRequireDataRefetch) {
            refetchRequired = this._customDataProviderImpl.doSettingsChangesRequireDataRefetch(
                this._prevSettings,
                this._settingsContextDelegate.getValues() as TSettingTypes,
                this.makeAccessors(),
            );
        } else {
            refetchRequired = !isEqual(this._settingsContextDelegate.getValues(), this._prevSettings);
        }

        if (!refetchRequired) {
            if (this._customDataProviderImpl.doStoredDataChangesRequireDataRefetch) {
                refetchRequired = this._customDataProviderImpl.doStoredDataChangesRequireDataRefetch(
                    this._prevStoredData,
                    this._settingsContextDelegate.getStoredDataRecord(),
                    this.makeAccessors(),
                );
            } else {
                refetchRequired = !isEqual(this._settingsContextDelegate.getStoredDataRecord(), this._prevStoredData);
            }
        }

        if (!refetchRequired) {
            // If the settings have changed but no refetch is required, it might be that the settings changes
            // still require a rerender of the data provider.
            if (this._status === DataProviderStatus.SUCCESS) {
                this.incrementRevisionNumber();
                return;
            }
            this.setStatus(DataProviderStatus.SUCCESS);
            return;
        }

        this._currentTransactionId += 1;
        const localTransactionId = this._currentTransactionId;

        // Debounce the refetch to avoid multiple refetches in a short time span.
        if (this._debounceTimeout) {
            clearTimeout(this._debounceTimeout);
        }
        this._debounceTimeout = setTimeout(() => {
            if (this._currentTransactionId !== localTransactionId) {
                // If the transaction id has changed, it means that a new transaction has started while the
                // previous one was still running. In this case, we do not refetch the data
                return;
            }
            this.maybeRefetchData().then(() => {
                if (this._currentTransactionId === localTransactionId) {
                    // Store the previous settings and stored data after the data has been fetched
                    this._prevSettings = clone(this._settingsContextDelegate.getValues()) as TSettingTypes;
                    this._prevStoredData = clone(this._settingsContextDelegate.getStoredDataRecord()) as TStoredData;
                }
            });
        }, 10);
    }

    private handleSettingsStatusChange(): void {
        const status = this._settingsContextDelegate.getStatus();
        if (status === SettingsContextStatus.INVALID_SETTINGS) {
            this._error = "Invalid settings";
            this.setStatus(DataProviderStatus.INVALID_SETTINGS);
            return;
        }
        if (status === SettingsContextStatus.LOADING) {
            this.setStatus(DataProviderStatus.LOADING);
            return;
        }
    }

    getStatus(): DataProviderStatus {
        return this._status;
    }

    getData(): TData | null {
        return this._data;
    }

    getType(): string {
        return this._type;
    }

    getItemDelegate() {
        return this._itemDelegate;
    }

    getSettingsContextDelegate() {
        return this._settingsContextDelegate;
    }

    isSubordinated(): boolean {
        return this._isSubordinated;
    }

    setIsSubordinated(isSubordinated: boolean): void {
        if (this._isSubordinated === isSubordinated) {
            return;
        }
        this._isSubordinated = isSubordinated;
        this._publishSubscribeDelegate.notifySubscribers(DataProviderTopic.SUBORDINATED);
    }

    getDataValueRange(): readonly [number, number] | null {
        return this._valueRange;
    }

    getDataProviderManager(): DataProviderManager {
        return this._dataProviderManager;
    }

    makeSnapshotGetter<T extends DataProviderTopic>(topic: T): () => DataProviderPayloads<TData>[T] {
        const snapshotGetter = (): any => {
            if (topic === DataProviderTopic.STATUS) {
                return this._status;
            }
            if (topic === DataProviderTopic.DATA) {
                return this._data;
            }
            if (topic === DataProviderTopic.SUBORDINATED) {
                return this._isSubordinated;
            }
            if (topic === DataProviderTopic.REVISION_NUMBER) {
                return this._revisionNumber;
            }
            if (topic === DataProviderTopic.PROGRESS_MESSAGE) {
                return this._progressMessage;
            }
            if (topic === DataProviderTopic.STATUS_MESSAGES) {
                return this._allStatusMessages;
            }
            throw new Error(`Unknown topic: ${topic}`);
        };

        return snapshotGetter;
    }

    getPublishSubscribeDelegate(): PublishSubscribeDelegate<DataProviderPayloads<TData>> {
        return this._publishSubscribeDelegate;
    }

    getError(): StatusMessage | string | null {
        if (!this._error) {
            return null;
        }

        const name = this.getItemDelegate().getName();

        if (typeof this._error === "string") {
            return `${name}: ${this._error}`;
        }

        return {
            ...this._error,
            message: `${name}: ${this._error.message}`,
        };
    }

    setProgressMessage(message: string | null): void {
        if (this._progressMessage === message) {
            return;
        }
        this._progressMessage = message;
        this._publishSubscribeDelegate.notifySubscribers(DataProviderTopic.PROGRESS_MESSAGE);
    }

    makeAccessors(): DataProviderAccessors<TSettings, TData, TStoredData, TSettingKey> {
        return {
            getSetting: (settingName) => this._settingsContextDelegate.getSettings()[settingName].getValue(),
            getSettingValueConstraints: (settingName) =>
                this._settingsContextDelegate.getSettings()[settingName].getValueConstraints(),
            getGlobalSetting: (settingName) => this._dataProviderManager.getGlobalSetting(settingName),
            getStoredData: (key: keyof TStoredData) => this._settingsContextDelegate.getStoredData(key),
            getData: () => this._data,
            getWorkbenchSession: () => this._dataProviderManager.getWorkbenchSession(),
            getWorkbenchSettings: () => this._dataProviderManager.getWorkbenchSettings(),
            getStatusWriter: () => this._statusWriter,
        };
    }

    private tidyUpFetchRelatedResources(): void {
        // Cancel any resources related to the last ongoing fetch.
        this._scopedQueryController.cancelActiveFetch();
        this._onFetchCancelOrFinishFn();
        this._onFetchCancelOrFinishFn = () => {};
    }

    private async maybeRefetchData(): Promise<void> {
        const thisTransactionId = this._currentTransactionId;

        const queryClient = this.getQueryClient();

        if (!queryClient) {
            return;
        }

        if (this._isSubordinated) {
            return;
        }

        const accessors = this.makeAccessors();

        this.invalidateValueRange();
        this.setProgressMessage(null);
        this.setStatus(DataProviderStatus.LOADING);

        const onFetchCancelOrFinish = (fnc: () => void) => {
            this._onFetchCancelOrFinishFn = fnc;
        };

        try {
            this._data = await this._customDataProviderImpl.fetchData({
                ...accessors,
                fetchQuery: <TQueryFnData, TError = Error, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
                    options: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
                ) => this._scopedQueryController.fetchQuery<TQueryFnData, TError, TData, TQueryKey>(options),
                onFetchCancelOrFinish,
                setProgressMessage: (message) => this.setProgressMessage(message),
            });

            // This is a security check to make sure that we are not using a stale transaction id.
            // This can happen if the transaction id is incremented while the async fetch data function is still running.
            // Queries are cancelled in the maybeCancelQuery function and should, hence, throw a cancelled error.
            // However, there might me some operations following after the query execution that are not cancelled.
            if (this._currentTransactionId !== thisTransactionId) {
                return;
            }

            if (this._customDataProviderImpl.makeValueRange) {
                this._valueRange = this._customDataProviderImpl.makeValueRange(accessors);
            }
            this._publishSubscribeDelegate.notifySubscribers(DataProviderTopic.DATA);
            this.setStatus(DataProviderStatus.SUCCESS);
        } catch (error: any) {
            if (isCancelledError(error)) {
                return;
            }
            const apiError = ApiErrorHelper.fromError(error);
            if (apiError) {
                this._error = apiError.makeStatusMessage();
            } else {
                if (typeof error === "string") {
                    this._error = error;
                } else if (error instanceof Error) {
                    this._error = error.message;
                }
            }
            this.setStatus(DataProviderStatus.ERROR);
        } finally {
            this._onFetchCancelOrFinishFn();
            this._onFetchCancelOrFinishFn = () => {};
            this.setProgressMessage(null);
        }
    }

    serializeState(): SerializedDataProvider<TSettings, TSettingKey> {
        const itemState = this.getItemDelegate().serializeState();
        return {
            ...itemState,
            type: SerializedType.DATA_PROVIDER,
            dataProviderType: this._type,
            settings: this._settingsContextDelegate.serializeSettings(),
        };
    }

    deserializeState(serializedDataProvider: SerializedDataProvider<TSettings, TSettingKey>): void {
        this.getItemDelegate().deserializeState(serializedDataProvider);
        const reportError = (errorMsg: string) => {
            this.getItemDelegate().reportDeserializationError(errorMsg);
        };
        this._settingsContextDelegate.deserializeSettings(serializedDataProvider.settings, reportError);
    }

    beforeDestroy(): void {
        this._settingsContextDelegate.beforeDestroy();
        this._unsubscribeFunctionsManagerDelegate.unsubscribeAll();
        this._scopedQueryController.cancelActiveFetch();
        if (this._debounceTimeout) {
            clearTimeout(this._debounceTimeout);
        }
    }

    private incrementRevisionNumber(): void {
        this._revisionNumber += 1;
        this._publishSubscribeDelegate.notifySubscribers(DataProviderTopic.REVISION_NUMBER);
        this._dataProviderManager.publishTopic(DataProviderManagerTopic.DATA_REVISION);
    }

    private setStatus(status: DataProviderStatus): void {
        if (this._status === status) {
            return;
        }

        this._status = status;
        this.incrementRevisionNumber();
        this._publishSubscribeDelegate.notifySubscribers(DataProviderTopic.STATUS);
    }

    private getQueryClient(): QueryClient | null {
        return this._dataProviderManager?.getQueryClient() ?? null;
    }

    private invalidateValueRange(): void {
        this._valueRange = null;
    }

    private syncAllStatusMessages(): void {
        const localMessages = this._statusWriter.getMessages();
        const settingsContextMessages = this._settingsContextDelegate.getStatusMessages();

        this._allStatusMessages = [...localMessages, ...settingsContextMessages];

        this._publishSubscribeDelegate.notifySubscribers(DataProviderTopic.STATUS_MESSAGES);
    }
}
